import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQuery'
import {
  AnnotationDataBoundingBox,
  Annotation,
  AnnotationPostResponse,
  QueryAnnotationsResp,
} from '../types/models/annotation'
import { AnnotationStatisticsResp, OkResp } from '../types/api/responses'
import { Filter } from '../types/common'
import { CreateBBoxAnnotationReq } from '../types/api/requests'
import { addAnnotatedContentIds } from '../store/slices/annotationSlice'
import {
  GET_ANNOTATIONS_STATISTICS_URL,
  GET_ANNOTATIONS_URL,
  POST_ANNOTATIONS_QUERY_URL,
} from '../constants'
import { apiTags } from './apiTags'
import { contentApi } from './contentApi'

export const annotationsApi = createApi({
  reducerPath: 'annotationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    apiTags.Annotation,
    apiTags.AnnotationStats,
    apiTags.Dataset,
    apiTags.DatasetList,
    apiTags.Tag,
    apiTags.PredictionList,
    apiTags.Prediction,
    apiTags.PredictionStats,
  ],

  endpoints: (builder) => ({
    createAnnotation: builder.mutation<AnnotationPostResponse, CreateBBoxAnnotationReq>({
      query: (annotationData) => ({
        url: GET_ANNOTATIONS_URL,
        method: 'POST',
        body: {
          ...annotationData,
          metadata: annotationData.metadata && {
            ...annotationData.metadata,
            bounding_boxes: annotationData.metadata.bounding_boxes
              ? transformBoundingBoxes(
                  annotationData.metadata.bounding_boxes,
                  annotationData.image_width,
                  annotationData.image_height
                )
              : undefined,
            oriented_bounding_boxes: annotationData.metadata.oriented_bounding_boxes
              ? transformBoundingBoxes(
                  annotationData.metadata.oriented_bounding_boxes,
                  annotationData.image_width,
                  annotationData.image_height
                )
              : undefined,
          },
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.Annotation, id: arg.content_id },
        { type: apiTags.AnnotationStats, id: arg.dataset_id },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Dataset, id: arg.dataset_id },
        { type: apiTags.DatasetList, id: 'LIST' },
        { type: apiTags.Tag, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.Prediction, id: arg.content_id },
        { type: apiTags.PredictionStats, id: arg.model_id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(addAnnotatedContentIds([data.id]))

          // Manually invalidate contentApi cache for this specific content
          dispatch(
            contentApi.util.invalidateTags([
              { type: 'ContentImage', id: arg.content_id },
              { type: 'Content', id: arg.content_id },
              { type: 'Content', id: 'LIST' },
            ])
          )

          // Force immediate image refresh by triggering custom event
          window.dispatchEvent(
            new CustomEvent('annotation-updated', {
              detail: { contentId: arg.content_id },
            })
          )

          // Also trigger reset-image-loading event for useContentImage hook
          window.dispatchEvent(new CustomEvent('reset-image-loading'))
        } catch (error) {
          console.error('Error in create annotation:', error)
        }
      },
    }),

    deleteAnnotations: builder.mutation<
      OkResp,
      {
        ids: string[]
        dataset_id: string
        content_ids?: string[]
        project_id: string
      }
    >({
      query: ({ ids }) => ({
        url: GET_ANNOTATIONS_URL,
        method: 'DELETE',
        params: { ids },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.AnnotationStats, id: arg.dataset_id },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Dataset, id: arg.dataset_id },
        { type: apiTags.DatasetList, id: 'LIST' },
        { type: apiTags.Tag, id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          // Manually invalidate contentApi cache for affected content
          if (arg.content_ids) {
            const invalidationTags = arg.content_ids.flatMap((contentId) => [
              { type: 'ContentImage' as const, id: contentId },
              { type: 'Content' as const, id: contentId },
            ])
            invalidationTags.push({ type: 'Content' as const, id: 'LIST' })

            dispatch(contentApi.util.invalidateTags(invalidationTags))

            // Force immediate image refresh for affected content
            arg.content_ids.forEach((contentId) => {
              window.dispatchEvent(
                new CustomEvent('annotation-updated', {
                  detail: { contentId },
                })
              )
            })
          }

          // Also trigger global reset
          window.dispatchEvent(new CustomEvent('reset-image-loading'))
        } catch (error) {
          console.error('Error in delete annotations:', error)
        }
      },
    }),

    queryAnnotations: builder.query<
      QueryAnnotationsResp,
      {
        filters?: Filter[]
        limit?: number
        operator?: string
        page?: number
        sortKey?: string
        sortVal?: number
        dataset_id?: string
      }
    >({
      query: (queryData) => ({
        url: POST_ANNOTATIONS_QUERY_URL,
        method: 'POST',
        body: { query: queryData },
      }),
      providesTags: (result, error, arg) => [
        { type: apiTags.Annotation, id: 'LIST' },
        ...(result?.annotations?.map(({ contentid }) => ({
          type: apiTags.Annotation,
          id: contentid,
        })) || []),
      ],
      // keepUnusedDataFor: 300,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(addAnnotatedContentIds(data.annotations.map((a) => a.contentid)))
        } catch (error) {
          console.error('Error in query annotations:', error)
        }
      },
    }),

    getAnnotationStatistics: builder.query<
      AnnotationStatisticsResp,
      {
        projectId: string
        datasetId: string
        stats?: string
        content_source?: string[]
        metadata_filter:
          | {
              datasource?: string[]
            }
          | undefined
      }
    >({
      query: ({ projectId, datasetId, stats, content_source, metadata_filter }) => ({
        url: GET_ANNOTATIONS_STATISTICS_URL,
        params: {
          project_id: projectId,
          dataset_id: datasetId,
          stats,
          content_source,
          metadata_filter: JSON.stringify(metadata_filter),
        },
      }),
      providesTags: (result, error, arg) => [
        { type: apiTags.AnnotationStats, id: arg.datasetId },
        { type: apiTags.AnnotationStats, id: 'LIST' },
      ],
      keepUnusedDataFor: 600,
    }),

    getAnnotation: builder.query<Annotation, string>({
      query: (id) => `/annotations/${id}`,
      providesTags: (result, error, id) => [{ type: apiTags.Annotation, id }],
      // keepUnusedDataFor: 300,
    }),

    deleteAnnotation: builder.mutation<
      OkResp,
      {
        id: string
        dataset_id: string
        content_id?: string
        project_id: string
      }
    >({
      query: ({ id }) => ({
        url: `/annotations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.Annotation, id: arg.id },
        { type: apiTags.AnnotationStats, id: arg.dataset_id },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Dataset, id: arg.dataset_id },
        { type: apiTags.DatasetList, id: 'LIST' },
        { type: apiTags.Tag, id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          // Manually invalidate contentApi cache for the specific content
          if (arg.content_id) {
            dispatch(
              contentApi.util.invalidateTags([
                { type: 'ContentImage', id: arg.content_id },
                { type: 'Content', id: arg.content_id },
                { type: 'Content', id: 'LIST' },
              ])
            )

            // Force immediate image refresh for the specific content
            window.dispatchEvent(
              new CustomEvent('annotation-updated', {
                detail: { contentId: arg.content_id },
              })
            )
          }

          // Also trigger global reset
          window.dispatchEvent(new CustomEvent('reset-image-loading'))
        } catch (error) {
          console.error('Error in delete annotation:', error)
        }
      },
    }),
  }),
})

function transformBoundingBoxes(
  boxes: AnnotationDataBoundingBox[],
  imageWidth?: number,
  imageHeight?: number
): Omit<AnnotationDataBoundingBox, 'class_index' | 'confidence' | 'class_name'>[] {
  return boxes.map(({ class_index, confidence, class_name, ...rest }) => {
    // If image dimensions are provided and points exist (UI sends pixel coordinates)
    if (imageWidth && imageHeight && rest.points && rest.points.length === 8) {
      // Check if points are already normalized (0-1) to avoid double conversion
      const maxX = Math.max(rest.points[0], rest.points[2], rest.points[4], rest.points[6])
      const maxY = Math.max(rest.points[1], rest.points[3], rest.points[5], rest.points[7])

      let normalizedPoints: number[]

      if (maxX <= 1 && maxY <= 1) {
        // Points are already normalized (0-1), use as-is
        normalizedPoints = [...rest.points]
      } else {
        // Points are in pixel coordinates, convert to percentage coordinates for backend (0-1)
        normalizedPoints = [
          rest.points[0] / imageWidth, // x1
          rest.points[1] / imageHeight, // y1
          rest.points[2] / imageWidth, // x2
          rest.points[3] / imageHeight, // y2
          rest.points[4] / imageWidth, // x3
          rest.points[5] / imageHeight, // y3
          rest.points[6] / imageWidth, // x4
          rest.points[7] / imageHeight, // y4
        ]
      }

      // Calculate min/max for legacy support
      let xmin: number, xmax: number, ymin: number, ymax: number

      if (maxX <= 1 && maxY <= 1) {
        // Points are normalized, convert to pixel coordinates for min/max
        const pixelCoords = normalizedPoints.map((coord, index) =>
          index % 2 === 0 ? coord * imageWidth : coord * imageHeight
        )
        const xCoords = [pixelCoords[0], pixelCoords[2], pixelCoords[4], pixelCoords[6]]
        const yCoords = [pixelCoords[1], pixelCoords[3], pixelCoords[5], pixelCoords[7]]

        xmin = Math.max(0, Math.min(imageWidth, Math.round(Math.min(...xCoords))))
        xmax = Math.max(0, Math.min(imageWidth, Math.round(Math.max(...xCoords))))
        ymin = Math.max(0, Math.min(imageHeight, Math.round(Math.min(...yCoords))))
        ymax = Math.max(0, Math.min(imageHeight, Math.round(Math.max(...yCoords))))
      } else {
        // Points are in pixel coordinates, use directly
        const xCoords = [rest.points[0], rest.points[2], rest.points[4], rest.points[6]]
        const yCoords = [rest.points[1], rest.points[3], rest.points[5], rest.points[7]]

        xmin = Math.max(0, Math.min(imageWidth, Math.round(Math.min(...xCoords))))
        xmax = Math.max(0, Math.min(imageWidth, Math.round(Math.max(...xCoords))))
        ymin = Math.max(0, Math.min(imageHeight, Math.round(Math.min(...yCoords))))
        ymax = Math.max(0, Math.min(imageHeight, Math.round(Math.max(...yCoords))))
      }

      return {
        ...rest,
        xmin,
        ymin,
        xmax,
        ymax,
        // Send normalized coordinates (0-1) to backend
        points: normalizedPoints,
      }
    }

    // Legacy fallback: If no points but has min/max, convert min/max to normalized points
    if (
      imageWidth &&
      imageHeight &&
      rest.xmin !== undefined &&
      rest.xmax !== undefined &&
      rest.ymin !== undefined &&
      rest.ymax !== undefined
    ) {
      // Convert pixel coordinates to normalized values (0-1)
      const xMinNorm = rest.xmin / imageWidth
      const xMaxNorm = rest.xmax / imageWidth
      const yMinNorm = rest.ymin / imageHeight
      const yMaxNorm = rest.ymax / imageHeight

      // Create points array for oriented bounding box (4 corners as normalized coordinates)
      // Points format: [X1, Y1, X2, Y2, X3, Y3, X4, Y4]
      // Order: top-left, top-right, bottom-right, bottom-left
      const points = [
        xMinNorm,
        yMinNorm, // Top-left
        xMaxNorm,
        yMinNorm, // Top-right
        xMaxNorm,
        yMaxNorm, // Bottom-right
        xMinNorm,
        yMaxNorm, // Bottom-left
      ]

      return {
        ...rest,
        points,
      }
    }

    // If no image dimensions provided, return as-is (excluding the omitted fields)
    return rest
  })
}

export const {
  useCreateAnnotationMutation,
  useDeleteAnnotationsMutation,
  useDeleteAnnotationMutation,
  useQueryAnnotationsQuery,
  useGetAnnotationStatisticsQuery,
  useGetAnnotationQuery,
} = annotationsApi

export const {
  util: { invalidateTags },
} = annotationsApi
